// Drawing markup with no connection (v4.38.11, Karan 03/11/2026: "make sure
// markups are never lost"). The logic with no DOM and no Firebase, so it can be
// tested in node: what the sheet says about its queued marks, what counts as a
// connection failure, and the Save runner that turns one click into exactly one
// _MKnn PDF however many times the connection comes and goes.

const plural = (n) => `${n} mark${n === 1 ? '' : 's'}`;

/**
 * The status line on the sheet. Grey and small unless the person must act
 * (CLAUDE.md P6): waiting for a connection is not their problem to solve.
 *   online    navigator.onLine
 *   pending   marks on this sheet the server has not confirmed (from Firestore)
 *   failed    writes the server REFUSED in this session
 *   fromCache the listener has not heard from the server since it started
 *   hasMarks  anything on this sheet at all
 */
export function syncLine({ online = true, pending = 0, failed = 0, fromCache = false, hasMarks = false } = {}) {
  if (failed > 0) return { text: `${plural(failed)} not saved`, tone: 'act', action: 'retry' };
  if (pending > 0) {
    if (!online) return { text: `Offline · ${plural(pending)} waiting to send`, tone: 'quiet' };
    // The browser says online but the server has not answered yet.
    return { text: fromCache ? `${plural(pending)} waiting to send` : `Sending ${plural(pending)}…`, tone: 'quiet' };
  }
  if (!online) return { text: 'Offline', tone: 'quiet' };
  return { text: hasMarks ? 'All saved' : '', tone: 'quiet' };
}

const CONNECTION = /failed to fetch|networkerror|network error|load failed|err_internet|err_network|err_connection|dynamically imported module|importing a module script failed|the internet connection appears to be offline/i;

/**
 * Did this fail because of the connection, so it is worth waiting for one? With
 * the browser offline, yes whatever the message says (a token that cannot be
 * renewed reads as "session expired"). Online, only a failure that looks like the
 * network: a refusal from Google, or Google not connected, has to reach the
 * person rather than wait for ever.
 */
export function isConnectionError(e, { online = true } = {}) {
  if (!online) return true;
  return CONNECTION.test(String((e && e.message) || e || ''));
}

/**
 * One click, one PDF.
 *   attempt({ token, retry }) does the work and resolves to the ref. `retry` means
 *     an earlier try may have got through (the reply can be what was lost), so it
 *     must look for `token` on Drive before uploading again.
 *   remember(token) / forget(): the intent, so a closed tab can offer it again.
 *   lock(fn): one attempt per sheet across tabs.
 * Phases: idle, saving, waiting (for a connection), saved, failed.
 */
export function createSaveRunner({
  attempt, isOnline, onState, remember, forget, newToken, lock = (fn) => fn(),
  retryMs = 30000, settleMs = 1500, setTimer = setTimeout, clearTimer = clearTimeout,
}) {
  let phase = 'idle';
  let token = null;
  let retry = false;
  let timer = null;
  let disposed = false;
  const set = (p, extra = {}) => { phase = p; if (!disposed) onState?.(p, extra); };
  const stopTimer = () => { if (timer != null) clearTimer(timer); timer = null; };

  function wait() {
    stopTimer();
    set('waiting');
    // The browser thinks it is online and it is not getting through (a dead
    // hotspot, a captive portal): no `online` event will ever come, so keep trying.
    if (isOnline() && !disposed) timer = setTimer(run, retryMs);
  }
  async function run() {
    stopTimer();
    if (disposed || phase === 'saving') return;
    if (!isOnline()) { wait(); return; }
    set('saving');
    const again = retry;
    retry = true;                        // whatever happens next, this try may have landed
    try {
      const ref = await lock(() => attempt({ token, retry: again }));
      forget?.();
      token = null; retry = false;
      set('saved', { ref });
    } catch (e) {
      if (isConnectionError(e, { online: isOnline() })) wait();
      else set('failed', { error: e });
    }
  }
  return {
    get phase() { return phase; },
    /** A click (or Save now on an offer). False when it changed nothing. */
    click({ resume = null } = {}) {
      if (disposed || phase === 'saving' || phase === 'waiting' || phase === 'saved') return false;
      if (resume && resume.token) { token = resume.token; retry = true; }
      if (!token) { token = newToken(); retry = false; }
      remember?.(token);
      run();
      return true;
    },
    /** The connection came back: one try, after it has settled. Repeats collapse into one. */
    online() {
      if (disposed || phase !== 'waiting') return;
      stopTimer();
      timer = setTimer(run, settleMs);
    },
    offline() { if (phase === 'waiting') stopTimer(); },
    /** Saved has been shown; the button is a plain Save again. */
    reset() { if (phase === 'saved') phase = 'idle'; },
    dispose() { disposed = true; stopTimer(); },
  };
}

// ── the intent, kept per drawing so a closed tab can offer it again ─────────
const INTENT_PREFIX = 'track.markupSave.';
const store = () => { try { return globalThis.localStorage || null; } catch (_) { return null; } };

export function readSaveIntent(fileId, s = store()) {
  try {
    const v = JSON.parse(s.getItem(INTENT_PREFIX + fileId));
    return v && typeof v.token === 'string' ? v : null;
  } catch (_) { return null; }
}
export function writeSaveIntent(fileId, token, s = store()) {
  try { s.setItem(INTENT_PREFIX + fileId, JSON.stringify({ token, at: Date.now() })); } catch (_) { /* storage blocked: this session still finishes it */ }
}
export function clearSaveIntent(fileId, s = store()) {
  try { s.removeItem(INTENT_PREFIX + fileId); } catch (_) { /* storage blocked */ }
}
/** Sign-out: the next person at this browser is not offered this person's PDF. */
export function clearAllSaveIntents(s = store()) {
  try { Object.keys(s).filter((k) => k.startsWith(INTENT_PREFIX)).forEach((k) => s.removeItem(k)); } catch (_) { /* storage blocked */ }
}

/** One Save per drawing across tabs, so two tabs can never take the same number. */
export function withSheetLock(name, fn, locks = globalThis.navigator && globalThis.navigator.locks) {
  return locks && locks.request ? locks.request(name, fn) : fn();
}
